import React, { Fragment } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { ImageComponent } from "./ImageComponent";
import { HeadingComponent } from "./HeadingComponent";
import { TextComponent } from "./TextComponent";
/**
 * Returns configurable ModalComponent.
 *
 * @example
 * Here's a simple example:
 * ```
 * import React, { useState } from "react";
 * import { ModalComponent, NblocksButton } from "@nebulr-group/nblocks-react";
 * import { svgIcon } from "../assets/svgIcon.svg";
 *
 *
 * const MyComponent = () => {
 *  // Modal open state
 *  const [ isOpen, setIsOpen ] = useState(false);
 *
 *  return (
 *   <ModalComponent
 *    isOpen={isOpen}
 *    setIsOpen={setIsOpen}
 *    heading={"Modal's Example Title!"}
 *    description={"Modal's example description."}
 *    icon={svgIcon}
 *    >
 *        <NblocksButton
 *          size="md"
 *          type="tertiary"
 *          onClick={() => setIsOpen(false)}
 *        >
 *          Cancel
 *        </NblocksButton>
 *        <NblocksButton
 *          size="md"
 *          type="primary"
 *          onClick={() => {
 *            alert("Saved!");
 *            setIsOpen(false);
 *          }}
 *         >
 *          Save changes
 *         </NblocksButton>
 *   </ModalComponent>
 *  );
 * };
 * ```
 *
 * @param param0
 * @returns
 */
const ModalComponent = ({
  isOpen,
  children,
  heading,
  description,
  icon,
  iconClassName,
  iconType,
  setIsOpen,
  width,
  overlayClassName
}) => {
  iconClassName = iconClassName ? iconClassName : "";
  const getModalIcon = icon => {
    if (typeof icon === "string") {
      return /*#__PURE__*/React.createElement(ImageComponent, {
        src: icon,
        className: `${iconClassName}${getIconTypeStyle(iconType)}`
      });
    } else {
      return /*#__PURE__*/React.createElement("div", {
        className: `${iconClassName}${getIconTypeStyle(iconType)}`
      }, icon);
    }
  };
  const getMaxWidthClassName = () => {
    return `max-w-${width ? width : "md"}`;
  };
  return /*#__PURE__*/React.createElement(Transition, {
    show: isOpen,
    enter: "transition duration-100 ease-out",
    enterFrom: "transform scale-95 opacity-0",
    enterTo: "transform scale-100 opacity-100",
    leave: "transition duration-75 ease-out",
    leaveFrom: "transform scale-100 opacity-100",
    leaveTo: "transform scale-95 opacity-0",
    as: Fragment
  }, /*#__PURE__*/React.createElement(Dialog, {
    onClose: () => setIsOpen(false),
    className: "fixed flex justify-center items-center inset-0 overflow-y-auto"
  }, /*#__PURE__*/React.createElement(Dialog.Overlay, {
    className: `fixed inset-0 bg-gray-400/30 ${overlayClassName ? overlayClassName : ""}`
  }), /*#__PURE__*/React.createElement(Dialog.Panel, {
    className: `relative w-full p-4 md:p-6 ${getMaxWidthClassName()} bg-white rounded-lg z-50`
  }, /*#__PURE__*/React.createElement("div", {
    className: `flex ${icon ? "justify-between" : "justify-end"} p-0`
  }, icon && /*#__PURE__*/React.createElement("div", {
    className: `border-8 w-14 h-14 rounded-full flex justify-center items-center${getIconBackgroundTypeStyle(iconType)}`
  }, getModalIcon(icon))), /*#__PURE__*/React.createElement("div", {
    className: "mt-5"
  }, heading && /*#__PURE__*/React.createElement(HeadingComponent, {
    size: "lg",
    type: "h2",
    className: "font-semibold"
  }, heading), description && /*#__PURE__*/React.createElement(TextComponent, {
    size: "sm",
    className: "mt-2 text-slate-900"
  }, description), children && /*#__PURE__*/React.createElement("div", {
    className: "mt-2"
  }, children)))));
};

/**
 *
 * @param imageType
 * @returns
 */
const getIconBackgroundTypeStyle = iconType => {
  switch (iconType) {
    case "danger":
      return " bg-red-200 border-red-100";
    case "success":
      return " bg-green-200 border-green-100";
    case "warning":
      return " bg-yellow-200 border-yellow-100";
    case "primary":
      return " bg-purple-200 border-purple-100";
    case "info":
      return " bg-blue-200 border-blue-100";
    default:
      return " bg-purple-200 border-purple-100";
  }
};
const getIconTypeStyle = iconType => {
  switch (iconType) {
    case "danger":
      return " text-red-600 h-5 w-5";
    case "success":
      return " text-green-600 h-5 w-5";
    case "warning":
      return " text-yellow-600 h-5 w-5";
    case "primary":
      return " text-purple-600 h-5 w-5";
    case "info":
      return " text-blue-600 h-5 w-5";
    default:
      return " text-purple-600 h-5 w-5";
  }
};
export { ModalComponent };